// ─── AuthGate.jsx ─────────────────────────────────────────────────────────
// Real backend RBAC login gate. Authenticates against the FastAPI
// /api/auth/* endpoints — no client-side credentials.
//
// Permissions are whatever the backend returns in the login response; the
// frontend does NOT keep its own role→permission map (which could drift from
// the server). canDo('permission_name') checks that server-provided list.
//
// USAGE — wrap the app in App.js:
//   import AuthGate, { useAuth } from './AuthGate';
//   export default function App() {
//     return <AuthGate><AppContent /></AuthGate>;
//   }
//   // In any component:
//   const { user, role, canDo } = useAuth();
//   {canDo('switch_model') && <button>Switch Model</button>}
//
// Permission names (must match backend permissions.py):
//   view_dashboard, view_alerts, view_incidents, view_intel, view_metrics,
//   view_audit_log, ack_tier1_alert, ack_tier2_alert, escalate_incident,
//   run_detection, upload_data, manage_stream, reset_state,
//   switch_model, configure_webhooks, manage_users
// ────────────────────────────────────────────────────────────────────────

import React, { useState, useEffect, createContext, useContext, useCallback } from 'react';
import { login as apiLogin, logout as apiLogout, changePassword as apiChangePassword, tokenStore } from './utils/apiClient';

const mono = { fontFamily: 'JetBrains Mono, monospace' };

// Display metadata only — the source of truth for permissions is the backend.
const ROLE_META = {
  admin:      { label: 'Admin',      color: '#8b5cf6' },
  analyst_t1: { label: 'Analyst T1', color: '#3b82f6' },
  analyst_t2: { label: 'Analyst T2', color: '#06b6d4' },
  guest:      { label: 'Guest',      color: '#6b7280' },
};

export const AuthContext = createContext(null);
export function useAuth() { return useContext(AuthContext); }

// ── Login form ────────────────────────────────────────────────────────────
function LoginForm({ onLogin }) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error,    setError]    = useState('');
  const [loading,  setLoading]  = useState(false);

  async function handleSubmit(e) {
    e?.preventDefault?.();
    if (!username || !password) return;
    setLoading(true);
    setError('');
    try {
      const user = await apiLogin(username, password);
      onLogin(user);
    } catch (err) {
      // 423 = locked account; surface that distinctly.
      if (err.status === 423) setError('Account temporarily locked — too many attempts');
      else setError(err.message || 'Invalid credentials');
    } finally {
      setLoading(false);
    }
  }

  const inputStyle = {
    width: '100%', padding: '9px 12px', background: '#0d1018',
    border: '1px solid #ffffff22', borderRadius: 6,
    color: '#e8eaf0', fontSize: 13, ...mono, outline: 'none',
    boxSizing: 'border-box',
  };

  return (
    <div style={{ minHeight: '100vh', background: '#0a0c10', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ width: 360, background: '#141720', border: '1px solid #ffffff14', borderRadius: 12, padding: 32 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 28 }}>
          <div style={{ width: 36, height: 36, background: 'linear-gradient(135deg,#ff3b4e,#8b5cf6)', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, fontWeight: 700, color: '#fff' }}>S</div>
          <div>
            <div style={{ fontSize: 15, fontWeight: 600, color: '#e8eaf0' }}>AEGIS SOC</div>
            <div style={{ fontSize: 10, color: '#4b5563', ...mono }}>Hybrid AE + LightGBM APT Detection v2.1</div>
          </div>
        </div>

        <div style={{ fontSize: 11, color: '#9ca3af', ...mono, letterSpacing: '.1em', marginBottom: 20 }}>SIGN IN TO CONTINUE</div>

        <div style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 10, color: '#6b7280', ...mono, marginBottom: 5 }}>USERNAME</div>
          <input style={inputStyle} value={username} onChange={e => setUsername(e.target.value)}
            autoComplete="username" onKeyDown={e => e.key === 'Enter' && handleSubmit()} />
        </div>
        <div style={{ marginBottom: 20 }}>
          <div style={{ fontSize: 10, color: '#6b7280', ...mono, marginBottom: 5 }}>PASSWORD</div>
          <input style={inputStyle} type="password" value={password} onChange={e => setPassword(e.target.value)}
            placeholder="••••••••" autoComplete="current-password"
            onKeyDown={e => e.key === 'Enter' && handleSubmit()} />
        </div>

        {error && (
          <div style={{ padding: '6px 10px', background: '#ff3b4e18', border: '1px solid #ff3b4e44', borderRadius: 5, fontSize: 11, color: '#ff6b7a', ...mono, marginBottom: 12 }}>
            {error}
          </div>
        )}

        <button onClick={handleSubmit} disabled={loading || !username || !password}
          style={{ width: '100%', padding: 10, borderRadius: 6, fontSize: 12, fontWeight: 500, cursor: 'pointer', border: '1px solid #3b82f644', background: '#3b82f622', color: '#60a5fa', ...mono, opacity: (loading || !username || !password) ? .5 : 1 }}>
          {loading ? '⟳ Signing in...' : '→ Sign In'}
        </button>
      </div>
    </div>
  );
}

// ── Forced password change (first login / admin reset) ─────────────────────
function ChangePasswordForm({ onDone, onCancelLogout }) {
  const [current, setCurrent] = useState('');
  const [next,    setNext]    = useState('');
  const [confirm, setConfirm] = useState('');
  const [error,   setError]   = useState('');
  const [loading, setLoading] = useState(false);

  async function submit() {
    setError('');
    if (next.length < 8) { setError('New password must be at least 8 characters'); return; }
    if (next !== confirm) { setError('Passwords do not match'); return; }
    setLoading(true);
    try {
      await apiChangePassword(current, next);
      onDone();
    } catch (err) {
      setError(err.status === 400 ? 'Current password is incorrect' : (err.message || 'Change failed'));
    } finally {
      setLoading(false);
    }
  }

  const inputStyle = {
    width: '100%', padding: '9px 12px', background: '#0d1018',
    border: '1px solid #ffffff22', borderRadius: 6, marginBottom: 12,
    color: '#e8eaf0', fontSize: 13, ...mono, outline: 'none', boxSizing: 'border-box',
  };

  return (
    <div style={{ minHeight: '100vh', background: '#0a0c10', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ width: 360, background: '#141720', border: '1px solid #ffffff14', borderRadius: 12, padding: 32 }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: '#e8eaf0', marginBottom: 6 }}>Change your password</div>
        <div style={{ fontSize: 11, color: '#9ca3af', ...mono, marginBottom: 20 }}>
          You must set a new password before continuing.
        </div>
        <input style={inputStyle} type="password" placeholder="Current password"
          value={current} onChange={e => setCurrent(e.target.value)} />
        <input style={inputStyle} type="password" placeholder="New password (min 8 chars)"
          value={next} onChange={e => setNext(e.target.value)} />
        <input style={inputStyle} type="password" placeholder="Confirm new password"
          value={confirm} onChange={e => setConfirm(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && submit()} />
        {error && (
          <div style={{ padding: '6px 10px', background: '#ff3b4e18', border: '1px solid #ff3b4e44', borderRadius: 5, fontSize: 11, color: '#ff6b7a', ...mono, marginBottom: 12 }}>
            {error}
          </div>
        )}
        <button onClick={submit} disabled={loading}
          style={{ width: '100%', padding: 10, borderRadius: 6, fontSize: 12, fontWeight: 500, cursor: 'pointer', border: '1px solid #3b82f644', background: '#3b82f622', color: '#60a5fa', ...mono, opacity: loading ? .5 : 1, marginBottom: 8 }}>
          {loading ? '⟳ Saving...' : 'Set password & continue'}
        </button>
        <button onClick={onCancelLogout}
          style={{ width: '100%', padding: 8, borderRadius: 6, fontSize: 11, cursor: 'pointer', border: '1px solid #ffffff14', background: 'transparent', color: '#6b7280', ...mono }}>
          Cancel & sign out
        </button>
      </div>
    </div>
  );
}

// ── Auth gate wrapper ──────────────────────────────────────────────────────
export default function AuthGate({ children }) {
  const [user, setUser] = useState(() => tokenStore.getUser());
  const [needsPwChange, setNeedsPwChange] = useState(() => !!tokenStore.getUser()?.must_change_password);

  // If the API client forces a logout (refresh failed), reflect it here.
  useEffect(() => {
    const handler = () => { setUser(null); setNeedsPwChange(false); };
    window.addEventListener('aegis:logout', handler);
    return () => window.removeEventListener('aegis:logout', handler);
  }, []);

  const handleLogin = useCallback((u) => {
    setUser(u);
    setNeedsPwChange(!!u.must_change_password);
  }, []);

  const logout = useCallback(async () => {
    await apiLogout();
    setUser(null);
    setNeedsPwChange(false);
  }, []);

  const canDo = useCallback(
    (permission) => Array.isArray(user?.permissions) && user.permissions.includes(permission),
    [user]
  );

  if (!user) return <LoginForm onLogin={handleLogin} />;

  if (needsPwChange) {
    return (
      <ChangePasswordForm
        onDone={() => {
          const updated = { ...user, must_change_password: false };
          tokenStore.set({ user: updated });
          setUser(updated);
          setNeedsPwChange(false);
        }}
        onCancelLogout={logout}
      />
    );
  }

  const roleInfo = ROLE_META[user.role] || { label: user.role, color: '#6b7280' };

  return (
    <AuthContext.Provider value={{ user, role: user.role, roleInfo, canDo, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

// ── Role badge (used in App.js header) ─────────────────────────────────────
export function RoleBadge() {
  const auth = useAuth();
  if (!auth) return null;
  const col = auth.roleInfo?.color || '#6b7280';
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <span style={{ fontSize: 11, ...mono, color: col, background: `${col}18`, border: `1px solid ${col}44`, padding: '3px 10px', borderRadius: 4 }}>
        {auth.user.username} · {auth.roleInfo?.label}
      </span>
      <button onClick={auth.logout}
        style={{ fontSize: 10, ...mono, color: '#6b7280', background: 'transparent', border: '1px solid #ffffff14', borderRadius: 4, padding: '3px 8px', cursor: 'pointer' }}>
        Sign out
      </button>
    </div>
  );
}